import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { LoginCredentials, LoginResponse, ForgotPasswordCredentials, ForgotPasswordResponse, VerifyOTPCredentials, VerifyOTPResponse, ResetPasswordCredentials, ResetPasswordResponse } from '../types/authTypes';
import axiosInstance from '../../axios';

// const API_URL = import.meta.env.VITE_BACKEND_URL! || 'http:/localhost:3000';

// Async Action: Login User
export const loginUser = createAsyncThunk<LoginResponse, LoginCredentials, { rejectValue: string }>(
  'auth/login',
  async ({ email, password, role, rememberMe }, { rejectWithValue }) => {
    try {
      // First, ensure we clear any existing tokens to prevent conflicts
      document.cookie = 'pms_admin_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'pms_employee_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      console.log(`Attempting login as ${role} for ${email}`);
      
      const response = await axios.post<LoginResponse>(`/api/v1/${role}/signin`, {
        email,
        password,
      });

      console.log('Login response received:', response.data);

      // Verify that the token is actually set in cookies
      const cookies = document.cookie;
      console.log('Cookies after login:', cookies);
      
      // Check if token cookie exists
      const tokenName = role === 'admin' ? 'pms_admin_' : 'pms_employee_';
      const hasTokenCookie = cookies.includes(tokenName);
      
      if (!hasTokenCookie) {
        console.warn('Token cookie not found after login. Setting manually.');
        // Try to manually set the cookie as a fallback
        if (response.data.token) {
          document.cookie = `${tokenName}=${response.data.token}; path=/; max-age=86400`;
        }
      }

      // If rememberMe is true, store credentials in localStorage
      if (rememberMe) {
        localStorage.setItem('rememberedCredentials', JSON.stringify({ email, role }));
      } else {
        // If not checked, remove any stored credentials
        localStorage.removeItem('rememberedCredentials');
      }

      return response.data; // { user, token }
    } catch (error: any) {
      // Log detailed error information for debugging
      console.error('Login error:', error);
      console.error('Response data:', error.response?.data);
      
      // Clear any partial auth state
      localStorage.removeItem('auth');
      document.cookie = 'pms_admin_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      document.cookie = 'pms_employee_=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      return rejectWithValue(error.response?.data?.message || 'Login failed');
    }
  }
);

// Async Action: Logout User
export const logoutUser = createAsyncThunk<void, { role: string }, { rejectValue: string }>(
  'auth/logout',
  async ({ role }, { rejectWithValue }) => {
    try {
      console.log(`Attempting to logout with role: ${role}`);

      // The correct endpoint is /admin/signout not /admin/logout
      const endpoint =
        role.toLowerCase() === 'admin' ? '/v1/admin/signout' : '/v1/employee/signout';
      console.log(`Using endpoint: ${endpoint}`);

      // Create a timeout promise to prevent hanging
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Logout request timeout')), 5000);
      });

      // Make the logout request with timeout
      const requestPromise = axiosInstance.post(endpoint);

      const response = await Promise.race([requestPromise, timeoutPromise]);

      console.log('Logout API response:', response.data);

      // Clear localStorage regardless of API response
      localStorage.removeItem('auth');
      localStorage.removeItem('users-data');

      return;
    } catch (error: any) {
      console.error('Logout error details:', error);
      
      // Even if API call fails, clear local storage
      localStorage.removeItem('auth');
      localStorage.removeItem('users-data');
      
      // Don't reject the promise for logout - always succeed locally
      console.log('Logout API failed but continuing with local cleanup');
      return;
    }
  }
);

export const forgotPassword = createAsyncThunk<
  ForgotPasswordResponse,
  ForgotPasswordCredentials,
  { rejectValue: string }
>('auth/forgotPassword', async ({ email, role }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/v1/auth/forgot-password', {
      email,
      role,
    });

    if (response.data.success) {
      // Detect if the user exists based on the response message
      const userDoesntExistMessage = "User with this email doesn't exist";
      const nonExistentPatterns = [
        "doesn't exist", 
        "does not exist", 
        "not found", 
        "no account"
      ];
      
      // Check if the response contains a message indicating user doesn't exist
      const userExists = !nonExistentPatterns.some(pattern => 
        response.data.message?.toLowerCase().includes(pattern)
      );
      
      return {
        ...response.data,
        userExists
      };
    }

    return rejectWithValue(response.data.message || 'Failed to process request');
  } catch (error: any) {
    console.error('Forgot password error:', error);
    
    // Check if the error message indicates user doesn't exist
    const errorMessage = error.response?.data?.message || 'Failed to process forgot password request';
    
    if (errorMessage.toLowerCase().includes("doesn't exist") || 
        errorMessage.toLowerCase().includes("does not exist") ||
        errorMessage.toLowerCase().includes("not found")) {
      // Return the error message to display it to the user
      return rejectWithValue(errorMessage);
    }
    
    return rejectWithValue(errorMessage);
  }
});

export const verifyOTP = createAsyncThunk<
  VerifyOTPResponse,
  VerifyOTPCredentials,
  { rejectValue: string }
>('auth/verifyOTP', async ({ email, otp, role }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/v1/auth/verify-otp', {
      email,
      otp,
      role,
    });

    if (response.data.success) {
      return response.data;
    }

    return rejectWithValue(response.data.message || 'Failed to verify OTP');
  } catch (error: any) {
    console.error('Verify OTP error:', error);
    return rejectWithValue(
      error.response?.data?.message || 'Failed to verify OTP'
    );
  }
});

export const resetPassword = createAsyncThunk<
  ResetPasswordResponse,
  ResetPasswordCredentials,
  { rejectValue: string }
>('auth/resetPassword', async ({ token, password, role }, { rejectWithValue }) => {
  try {
    const response = await axiosInstance.post('/v1/auth/reset-password', {
      token,
      password,
      role,
    });

    if (response.data.success) {
      return response.data;
    }

    return rejectWithValue(response.data.message || 'Failed to reset password');
  } catch (error: any) {
    console.error('Reset password error:', error);
    return rejectWithValue(
      error.response?.data?.message || 'Failed to reset password'
    );
  }
});
